import { useState } from 'react'
import { toast } from 'sonner'
import { CircleCheck, SignalHigh, UserPlus, Undo2, Workflow, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { StageDot } from '@/components/common/badges'
import { runBulk } from '@/lib/bulk'
import { fieldByName, queryClient, useIssueFields, useRepoMeta } from '@/lib/query'
import { refetchAfterBulk, type IssueWriteKind, type Receipt, type WriteVars } from '@/lib/issue-write'
import {
  closeWrite,
  reopenWrite,
  setAssigneesWrite,
  setFieldWrite,
  useCloseIssue,
  useReopenIssue,
  useSetAssignees,
  useSetIssueField,
} from '@/lib/mutations'
import { isManuallySettableStage } from '@/lib/stage'
import type { WorkIssue } from '@/types/github'

/** Which actions the bar offers, by the screen it sits on. Work issues get the
 *  full set (assign / priority / stage / close); rollout issues are bot-owned so
 *  only assignment (who's driving a failed one); closed issues get reopen. */
export type BulkKind = 'work' | 'closed' | 'rollout'

export function BulkActionBar({
  issues,
  kind,
  onClear,
}: {
  issues: WorkIssue[]
  kind: BulkKind
  onClear: () => void
}) {
  const setAssignees = useSetAssignees()
  const setField = useSetIssueField()
  const closeIssue = useCloseIssue()
  const reopen = useReopenIssue()
  const users = useRepoMeta().data?.assignableUsers ?? []
  const fields = useIssueFields().data
  const priorityOptions = fieldByName(fields, 'Priority')?.options.map((o) => o.name) ?? []
  // Only human-settable stages — Merged/Released/Done are stage-sync's to set.
  const stageOptions = (fieldByName(fields, 'Stage')?.options.map((o) => o.name) ?? []).filter(
    isManuallySettableStage,
  )
  const [busy, setBusy] = useState(false)
  const [pickOpen, setPickOpen] = useState(false)

  const n = issues.length

  function report(verb: string, ok: number, failed: number) {
    const msg = `${verb} ${ok} issue${ok === 1 ? '' : 's'}`
    if (failed) toast.warning(`${msg} · ${failed} failed`)
    else toast.success(msg)
  }

  async function run<V extends WriteVars>(kind: IssueWriteKind<V>, perItem: (issue: WorkIssue) => Promise<Receipt>, verb: string) {
    setBusy(true)
    const receipts: Receipt[] = []
    const { ok, failed } = await runBulk(issues, async (issue) => receipts.push(await perItem(issue)))
    setBusy(false)
    refetchAfterBulk(queryClient, kind, receipts)
    report(verb, ok, failed)
    onClear()
  }

  async function assign(login: string) {
    setPickOpen(false)
    await run(
      setAssigneesWrite,
      (issue) =>
        setAssignees.mutateAsync({
          issue,
          logins: Array.from(new Set([...issue.assignees.map((a) => a.login), login])),
          silent: true,
        }),
      `Assigned @${login} to`,
    )
  }

  const setPriority = (value: string) =>
    run(
      setFieldWrite,
      (issue) => setField.mutateAsync({ issue, fieldName: 'Priority', value, silent: true }),
      `Set ${value} priority on`,
    )

  const setStage = (value: string) =>
    run(
      setFieldWrite,
      (issue) => setField.mutateAsync({ issue, fieldName: 'Stage', value, silent: true }),
      `Moved to ${value} —`,
    )

  const closeAll = () => run(closeWrite, (issue) => closeIssue.mutateAsync({ issue, silent: true }), 'Closed')

  const reopenAll = () => run(reopenWrite, (issue) => reopen.mutateAsync({ issue, silent: true }), 'Reopened')

  return (
    <div className="border-border bg-surface flex items-center gap-2 border-b px-4 py-2 text-sm">
      <span className="text-muted-foreground tabular-nums">{n} selected</span>
      <div className="flex-1" />

      {(kind === 'work' || kind === 'rollout') && (
        <Popover open={pickOpen} onOpenChange={setPickOpen}>
          <PopoverTrigger asChild>
            <Button size="sm" variant="secondary" disabled={busy}>
              <UserPlus className="mr-1.5 size-3.5" />
              Assign
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-60 p-0">
            <Command>
              <CommandInput placeholder="Assign to…" />
              <CommandList>
                <CommandEmpty>No one found.</CommandEmpty>
                <CommandGroup>
                  {users.map((u) => (
                    <CommandItem key={u.login} value={`${u.login} ${u.name ?? ''}`} onSelect={() => assign(u.login)}>
                      {u.name ?? u.login}
                      <span className="text-muted-foreground ml-1.5 text-xs">@{u.login}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      {kind === 'work' && (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="secondary" disabled={busy || priorityOptions.length === 0}>
                <SignalHigh className="mr-1.5 size-3.5" />
                Priority
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {priorityOptions.map((p) => (
                <DropdownMenuItem key={p} onSelect={() => setPriority(p)}>
                  {p}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="secondary" disabled={busy || stageOptions.length === 0}>
                <Workflow className="mr-1.5 size-3.5" />
                Stage
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {stageOptions.map((s) => (
                <DropdownMenuItem key={s} onSelect={() => setStage(s)}>
                  <StageDot stage={s} className="mr-1.5" />
                  {s}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="secondary" disabled={busy}>
                <CircleCheck className="mr-1.5 size-3.5" />
                Close
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Close {n} issue{n === 1 ? '' : 's'} as completed?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  They’re marked Completed and leave the open views. The standard
                  close/verification handling runs per issue; reopen individually if needed.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={closeAll}>Close {n}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}

      {kind === 'closed' && (
        <Button size="sm" variant="secondary" disabled={busy} onClick={reopenAll}>
          <Undo2 className="mr-1.5 size-3.5" />
          Reopen
        </Button>
      )}

      <Button size="sm" variant="ghost" disabled={busy} onClick={onClear}>
        <X className="mr-1.5 size-3.5" />
        Clear
      </Button>
    </div>
  )
}
